'use client';

import React, { useEffect, useState } from 'react';
import styles from './styles/FullReviewsModal.module.css';
import ReviewAvatar from './reviewAvatar/ReviewAvatar';
import Link from 'next/link';

const DEFAULT_AVATAR = '/images/demo-profile.png';

function getReviewerName(review) {
  return review.userName || review.reviewerName || 'Anonymous';
}

function getReviewAvatar(review) {
  return review.avatarUrl || DEFAULT_AVATAR;
}

function getReviewProfileHref(review) {
  return review.profileHref || (review.userId ? `/public-profile/${review.userId}` : null);
}

function formatReviewDate(postedAt) {
  return postedAt ? new Date(postedAt).toLocaleDateString() : '';
}

async function fetchReviewsPage(courseId, limit, offset) {
  const res = await fetch(
    `/api/classes/${courseId}/reviews?limit=${limit}&offset=${offset}`
  );
  if (!res.ok) {
    throw new Error('Failed to fetch reviews');
  }
  return res.json();
}

export default function FullReviewsModal({
  isOpen,
  onClose,
  courseId,
}) {
  const [allReviews, setAllReviews] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 6;
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    if (!isOpen) return;

    const initializeReviews = async () => {
      try {
        const data = await fetchReviewsPage(courseId, pageSize, 0);
        setAllReviews(data.reviews || []);
        setTotalCount(data.totalCount || 0);
      } catch {
        // do nothing
      }
    };

    initializeReviews();
    setCurrentPage(1);
  }, [courseId, isOpen, pageSize]);

  const loadAllReviews = async (offset) => {
    try {
      const data = await fetchReviewsPage(courseId, pageSize, offset);
      setAllReviews(data.reviews || []);
      setTotalCount(data.totalCount || 0);
    } catch {
      // do nothing
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target.dataset.closeModal) {
      onClose();
    }
  };

  if (!isOpen) return null;

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  const handlePrevPage = () => {
    if (currentPage > 1) {
      const newPage = currentPage - 1;
      setCurrentPage(newPage);
      loadAllReviews((newPage - 1) * pageSize);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      const newPage = currentPage + 1;
      setCurrentPage(newPage);
      loadAllReviews((newPage - 1) * pageSize);
    }
  };

  return (
    <div
      className={styles.modal}
      aria-hidden={isOpen ? 'false' : 'true'}
      onClick={handleOverlayClick}>
      <div className={styles.modalOverlay} data-close-modal />
      <div className={styles.modalDialog} role="dialog" aria-modal="true">
        <button
          className={styles.modalClose}
          aria-label="Close modal"
          data-close-modal
        >
          &times;
        </button>
        <h2 id="moreReviewsTitle">All Reviews</h2>

        <div className={styles.reviewsGrid}>
          {allReviews.map((review) => (
            <ReviewCard key={review.id} review={review} />
          ))}
        </div>

        <div className={styles.pagination}>
          <button
            onClick={handlePrevPage}
            disabled={currentPage === 1}
            className={styles.paginationButton}
          >
            &lt;
          </button>
          <button
            onClick={handleNextPage}
            disabled={currentPage === totalPages}
            className={styles.paginationButton}
          >
            &gt;
          </button>
        </div>

        <p className={styles.reviewRange}>
          Page {currentPage} of {totalPages} (Total: {totalCount})
        </p>
      </div>
    </div>
  );
}

function ReviewCard({ review }) {
  const [expanded, setExpanded] = useState(false);
  const avatarPath = getReviewAvatar(review);
  const reviewerName = getReviewerName(review);
  const profileHref = getReviewProfileHref(review);

  const TRUNCATE_LENGTH = 120;
  const isLong = review.content.length > TRUNCATE_LENGTH;
  const shownText = expanded
    ? review.content
    : review.content.slice(0, TRUNCATE_LENGTH);

  return (
    <div className={styles.reviewItem}>
      {/* Top: rating only */}
      <div className={styles.ratingTop}>
        <StarRatingDisplay rating={review.rating} />
      </div>

      <blockquote>
        {shownText}
        {!expanded && isLong && <span>... </span>}
        {isLong && (
          <span
            onClick={() => setExpanded(!expanded)}
            className={styles.showRestToggle}
          >
            {expanded ? ' Show less' : ' Show rest'}
          </span>
        )}
      </blockquote>

      {/* Bottom-left: avatar + username, bottom-right: date */}
      <div className={styles.cardFooter}>
        <div className={styles.footerLeft}>
          <ReviewAvatar storedImage={avatarPath} />
          {profileHref ? (
            <Link href={profileHref} className={styles.userName}>
              {reviewerName}
            </Link>
          ) : (
            <span className={styles.userName}>{reviewerName}</span>
          )}
        </div>
        <div className={styles.footerRight}>
          {formatReviewDate(review.postedAt)}
        </div>
      </div>
    </div>
  );
}

function StarRatingDisplay({ rating }) {
  const stars = [];
  for (let i = 1; i <= 5; i++) {
    stars.push(
      <span
        key={i}
        className={i <= rating ? styles.starFilled : styles.starEmpty}
      >
        ★
      </span>
    );
  }
  return <div className={styles.ratingStars}>{stars}</div>;
}
